/* eslint-disable security/detect-object-injection */
import { ReduxContext, VSConfiguration } from '@bees-web/core/types'
import { blacklist, globals } from '@bees-web/nfa-interactive-global'
import { middlewares as webLinkCart } from '@bees-web/web-interactive-link-cart'
import { combineReducers, configureStore, Reducer } from '@reduxjs/toolkit'
import { persistReducer, persistStore } from 'redux-persist'
import thunk from 'redux-thunk'

import { actionsLoggerMiddleware } from '@/features/devtools/middleware/actionsLoggerMiddleware'
import { segmentMiddleware } from '@/features/segment'

import { storage } from '../utils/createWebStorage'
import { setSharedContext } from '../utils/VL/context/sharedContext'

const version = parseInt(process.env.BUILD_BUILDID)

const staticReducers = {
  globals: persistReducer(
    {
      key: `new-web-react-globals-${version}`,
      storage,
      blacklist,
      version,
    },
    globals
  ),
}

const configureReducer = (vsContext: ReduxContext): Reducer => {
  const { reducer, persist } = vsContext.state

  return persist
    ? persistReducer(
        {
          storage,
          ...persist(version),
        },
        reducer
      )
    : reducer
}

const createReducer = (asyncReducers: Record<string, Reducer>): Reducer =>
  combineReducers({
    ...staticReducers,
    ...asyncReducers,
  })

const store = configureStore({
  reducer: createReducer({}),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(thunk, segmentMiddleware, webLinkCart(), actionsLoggerMiddleware),
})

let currentReducerKeys: string[] = []
let persistor: ReturnType<typeof persistStore> | null = null

export const configureContextualStore = (
  dependencyMap: Record<string, VSConfiguration>
) => {
  const reducerKeys = Object.keys(dependencyMap).sort((a, b) => a.localeCompare(b))

  // Avoid unnecessary reconfiguration
  if (JSON.stringify(currentReducerKeys) === JSON.stringify(reducerKeys)) {
    return
  }

  currentReducerKeys = reducerKeys
  setSharedContext(dependencyMap)

  const reducers = Object.keys(dependencyMap).reduce<Record<string, Reducer>>(
    (acc, curr) => {
      if (dependencyMap[curr].redux)
        acc[curr] = configureReducer(dependencyMap[curr].redux)
      return acc
    },
    {}
  )

  const combinedReducer = createReducer(reducers)
  store.replaceReducer(combinedReducer)

  console.log(
    '🔁 Store reconfigured with reducers:',
    reducerKeys,
    '| Static reducers:',
    Object.keys(staticReducers)
  )

  if (!persistor) {
    // @ts-expect-error no type available for persistStore
    persistor = persistStore(store, { manualPersist: false })
  }
  persistor.persist()
}

export type AppState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export default store
